import React, { useState, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import { FieldArray, Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import dayjs from 'dayjs';
import * as fileApi from '../../../lib/api/file';
import _ from 'lodash';
import ProjectMultiFileForm from '../../../components/projectmng/files/ProjectMultiFileForm';
import { useDispatch, useSelector } from 'react-redux';

import { listProjectFiles } from '../../../modules/projectModule/projectFiles';
import { listProjectCosts } from '../../../modules/projectModule/projectCosts';

const validationSchema = yup.object().shape({
  files: yup.array().of(
    yup.object().shape({
      sellPartnerUuid: yup.string().required('매입처를 선택하세요'),
      fileAttrVal5: yup
        .date()
        .nullable()
        .default(undefined)
        .required('받은(수신)일자를 입력하세요'),

      fileDesc: yup.string().nullable('비고를 입력하세요'),
      file: yup.mixed().required('파일을 선택하세요'),
    }),
  ),
});

const ProjectMultiFileFormContainer = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [sellPartnerOptions, setSellPartnerOptions] = useState([]);

  const { project, costs } = useSelector(({ projectModule }) => ({
    project: projectModule.project.project,
    costs: projectModule.projectCosts.costs,
  }));

  useEffect(() => {
    if (project) {
      dispatch(listProjectCosts(project?.prjUuid));
    }
  }, [project, dispatch]);

  useEffect(() => {
    if (costs) {
      const options = _.uniqBy(costs, 'sellPartnerUuid');
      setSellPartnerOptions(options);
    }
  }, [costs]);

  const onSave = async (value) => {
    const selectedPartner = _.find(
      sellPartnerOptions,
      (item) => item.sellPartnerUuid === value.sellPartnerUuid,
    );

    const fileAttrVal5 = value?.fileAttrVal5
      ? dayjs(value?.fileAttrVal5).format('YYYY-MM-DD')
      : dayjs(new Date()).format('YYYY-MM-DD');

    let formData = new FormData();
    formData.append('fileDt', value.file);
    formData.append('fileTyp', 'project');
    formData.append('fileAttrVal1', project.prjUuid);
    formData.append('fileAttrVal2', '매입견적서');
    formData.append('fileAttrVal3', value.sellPartnerUuid); //매입처
    formData.append('fileAttrVal5', fileAttrVal5);
    formData.append('fileAttrVal4', selectedPartner?.saleVal); //매출금액
    formData.append('fileDesc', value.fileDesc);

    try {
      const response = await fileApi.addFiles(formData);
      if (response.data) {
        dispatch(listProjectFiles({ prjUuid: project.prjUuid }));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  return (
    <Box sx={{ my: 1 }}>
      <Formik
        initialValues={{
          files: [
            {
              sellPartnerUuid: '',
              fileAttrVal5: new Date(),
              fileDesc: '',
              file: null,
            },
          ],
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { resetForm }) => {
          // console.log("onSubmit", JSON.stringify(values, null, 2));
          if (values.files.length > 0) {
            values.files.forEach((value) => {
              onSave(value);
            });
            resetForm();
          }
        }}
      >
        {({
          isSubmitting,
          values,
          touched,
          dirty,
          errors,
          handleChange,
          handleBlur,
          isValid,
          arrayHelpers,
        }) => (
          <Form noValidate autoComplete="off">
            <FieldArray
              name="files"
              render={(arrayHelpers) => (
                <Box>
                  <Box>
                    {values.files.map((f, index) => {
                      return (
                        <ProjectMultiFileForm
                          key={index}
                          file={f}
                          sellPartnerOptions={sellPartnerOptions}
                          index={index}
                          touched={touched}
                          errors={errors}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          handleRemove={() => arrayHelpers.remove(index)}
                        />
                      );
                    })}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      my: 2,
                    }}
                  >
                    <Button
                      sx={{ mx: 2 }}
                      color="secondary"
                      variant="outlined"
                      onClick={() =>
                        arrayHelpers.push({
                          sellPartnerUuid: '',
                          fileAttrVal5: new Date(),
                          fileDesc: '',
                          file: null,
                        })
                      }
                    >
                      파일 추가
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      disabled={
                        !(dirty && isValid) ||
                        values.files.length === 0 ||
                        isSubmitting
                      }
                    >
                      저장
                    </Button>
                  </Box>
                </Box>
              )}
            ></FieldArray>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ProjectMultiFileFormContainer;
